import { useEffect, useRef, useCallback } from 'react';

const OPTIONS = {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
} as const;

export function useIntersectionObserver<T extends HTMLElement>(
    onIntersect: (() => void) | undefined,
    observerOptions: IntersectionObserverInit = OPTIONS,
) {
    const observerRef = useRef<IntersectionObserver | null>(null);
    const callbackRef = useRef(onIntersect);

    useEffect(() => {
        callbackRef.current = onIntersect;
    }, [onIntersect]);

    const ref = useCallback(
        (node: T) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }

            if (!node) {
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    const [first] = entries;

                    if (first.isIntersecting) {
                        callbackRef.current?.();
                    }
                },
                { ...OPTIONS, ...observerOptions },
            );

            if (node) {
                observer.observe(node);
                observerRef.current = observer;
            }
        },
        [observerOptions],
    );

    useEffect(() => {
        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
            }
        };
    }, []);

    return ref;
}
